import React, { useMemo } from "react";
import "./NewsHighlightsPanel.css";

const buildNewsUrl = (id) =>
  `${process.env.REACT_APP_CLIENT_URL}visualizarNoticiaNewsletter/${id}`;

function formatDate(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("pt-BR");
}

function mediaLabel(mediaType) {
  const m = String(mediaType || "").toLowerCase();
  if (m === "tv") return "TV";
  if (m === "radio") return "Rádio";
  if (m === "impresso" || m === "jornal") return "Impresso";
  if (m === "site" || m === "web") return "Web";
  return "Outros";
}

function mediaClass(mediaType) {
  const m = String(mediaType || "").toLowerCase();
  if (m === "tv") return "tv";
  if (m === "radio") return "radio";
  if (m === "newspaper" || m === "jornal") return "newspaper";
  if (m === "site" || m === "web") return "web";
  return "other";
}

function highlightLabel(types = []) {
  if (types.includes("marketing_value") && types.includes("audience")) {
    return "Valoração + Audiência";
  }

  if (types.includes("marketing_value")) {
    return "Por Valoração";
  }

  if (types.includes("audience")) {
    return "Por Audiência";
  }

  if (types.includes("featured") || types.includes("filter")) {
    return "Destaque";
  }

  return null;
}

function formatValue(item) {
  const hasMV = item?.types?.includes("marketing_value");
  const hasAudience = item?.types?.includes("audience");

  const parts = [];

  if (hasMV && item?.marketing_value) {
    const v = Number(item.marketing_value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
    parts.push(`Valoração: ${v}`);
  }

  if (hasAudience && item?.audience) {
    const a = Number(item.audience).toLocaleString("pt-BR");
    parts.push(`Audiência: ${a}`);
  }

  return parts.length ? parts.join(" • ") : null;
}

function pickThumb(thumbnails) {
  if (!Array.isArray(thumbnails) || !thumbnails.length) return null;
  return thumbnails[thumbnails.length - 1] || thumbnails[0];
}

function Thumb({ item, className = "", exportMode = false }) {
  const thumb = pickThumb(item?.thumbnails);
  const label = mediaLabel(item?.media_type);

  if (thumb) {
    return (
      <div className={`nhp3-thumb ${className}`}>
        <img src={thumb} alt="" />
      </div>
    );
  }

  return (
    <div className={`nhp3-thumb nhp3-thumb--ph ${className}`}>
      <div className="nhp3-ph-inner">
        <div className="nhp3-ph-type">{label}</div>
      </div>
    </div>
  );
}


export default function NewsHighlightsPanel({
  data,
  loading = false,
  exportMode = false,
  maxItems = 6,
}) {
  const items = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];

    const map = new Map();

    arr.forEach((item) => {
      const key = String(item.id);
      const existing = map.get(key);

      if (!existing) {
        map.set(key, {
          ...item,
          types: [item.type],
          marketing_value: item.marketing_value,
          audience: item.audience,
        });
      } else {
        existing.types = [...new Set([...existing.types, item.type])];

        if (item.type === "marketing_value") {
          existing.marketing_value = item.marketing_value;
        }

        if (item.type === "audience") {
          existing.audience = item.audience;
        }
      }
    });

    return Array.from(map.values()).slice(0, maxItems);
  }, [data, maxItems]);

  const handleOpen = (id) => {
    if (!id || exportMode) return;
    window.open(buildNewsUrl(id), "_blank", "noopener,noreferrer");
  };

  if (loading) return null;
  if (!items.length) return null;

  return (
    <div className={`nhp3 ${exportMode ? "nhp3--export" : ""}`}
     data-export-type="highlights"
     >

      <div className={`nhp3-grid ${exportMode ? "nhp3-grid--export" : ""}`}>
        {items.map((item, idx) =>
          !exportMode ? (
            <ListCard key={item.id || idx} item={item} onOpen={handleOpen} />
          ) : (
            <ExportRow key={item.id || idx} item={item} rank={idx + 1} />
          )
        )}
      </div>
    </div>
  );
}

function ListCard({ item, onOpen }) {
  const thumb = pickThumb(item?.thumbnails);
  const label = mediaLabel(item?.media_type);
  const mClass = mediaClass(item?.media_type);
  const highlight = highlightLabel(item?.types);
  const value = formatValue(item);
  const subject = item?.subject;

  return (
    <div className="nhp3-list" onClick={() => onOpen(item?.id)}>
      <div className="nhp3-list-media">
        {thumb ? (
          <img src={thumb} alt="" />
        ) : (
          <div className={`nhp3-list-ph ${mClass}`}>{label}</div>
        )}
      </div>

      <div className="nhp3-list-body">
        <div className="nhp3-list-title">{item.title}</div>

        <div className="nhp3-list-meta">
          <span className={`nhp3-badge ${mClass}`}>{label}</span>

            {highlight && (
              <span className="nhp3-highlight-type">
                <strong>Critério:</strong> {highlight}
              </span>
            )}

            {subject && !item?.types?.includes("featured") && !item?.types?.includes("filter") && (
              <span className="nhp3-subject">
                <strong>Assunto:</strong> {subject}
              </span>
            )}

          {value && (
            <span className="nhp3-highlight-value">
              {value}
            </span>
          )}

          <span className="nhp3-date">
            {formatDate(item?.publish_date)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ExportRow({ item, rank = 1 }) {
  const title = String(item?.title || "Notícia").trim();
  const highlight = highlightLabel(item?.types);
  const value = formatValue(item);
  const subject = item?.subject;

  return (
    <div className="nhp3-erow">
      <div className="nhp3-rank">{rank}</div>

      <Thumb item={item} className="nhp3-erowMedia" exportMode />

      <div className="nhp3-erowBody">
        <div className="nhp3-erowTitle" title={title}>
          {title}
        </div>

        <div className="nhp3-erowMeta">
          <span className="nhp3-erowKV">
            <span className="nhp3-erowK">Tipo</span>
            <span className="nhp3-erowV">
              {mediaLabel(item?.media_type)}
            </span>
          </span>

          {highlight && (
            <>
              <span className="nhp3-erowSep">•</span>
              <span className="nhp3-erowKV">
                <span className="nhp3-erowK">Critério</span>
                <span className="nhp3-erowV">{highlight}</span>
              </span>
            </>
          )}

          {subject && !item?.types?.includes("featured") && !item?.types?.includes("filter") && (
            <>
              <span className="nhp3-erowSep">•</span>
              <span className="nhp3-erowKV">
                <span className="nhp3-erowK">Assunto</span>
                <span className="nhp3-erowV">{subject}</span>
              </span>
            </>
          )}

          {value && (
            <>
              <span className="nhp3-erowSep">•</span>
              <span className="nhp3-erowKV">
                <span className="nhp3-erowK">Valores</span>
                <span className="nhp3-erowV">{value}</span>
              </span>
            </>
          )}

          <span className="nhp3-erowSep">•</span>

          <span className="nhp3-erowKV">
            <span className="nhp3-erowK">Data</span>
            <span className="nhp3-erowV">
              {formatDate(item?.publish_date)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

